import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin } from "lucide-react";
import { BtnLink, Eyebrow } from "@/components/site/Primitives";
import { useLink, useT } from "@/lib/i18n";

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const t = useT();
  const link = useLink();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "8%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pb-20 pt-32 sm:pt-36 lg:pb-28 lg:pt-44">
      <svg className="pointer-events-none absolute -right-40 top-0 h-[900px] w-[900px] text-ivory-2" viewBox="0 0 600 600" aria-hidden="true">
        <path d="M600 0v600C470 560 380 470 360 340 330 160 470 80 600 0z" fill="currentColor" />
      </svg>
      <div className="container-x relative grid items-center gap-14 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-6">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease }}>
            <Eyebrow>{t.hero.eyebrow}</Eyebrow>
          </motion.div>
          <h1 data-testid="hero-title" className="mt-7 font-display text-[2.9rem] font-medium leading-[0.98] text-forest sm:text-6xl lg:text-[5.1rem]">
            {t.hero.title.map((line, i) => (
              <motion.span
                key={line}
                className={`block ${i === 2 ? "italic text-gold-text" : ""}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease, delay: 0.15 + i * 0.14 }}
              >
                {line}
              </motion.span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease, delay: 0.6 }} className="mt-8 max-w-xl text-base leading-relaxed text-forest/80 sm:text-lg" data-testid="hero-text">
            {t.hero.text}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease, delay: 0.75 }} className="mt-10 flex flex-col gap-4 sm:flex-row">
            <BtnLink to={link("apply")} data-testid="hero-apply-btn">{t.hero.cta1}</BtnLink>
            <BtnLink to={link("services")} variant="outline" arrow={false} data-testid="hero-services-btn">{t.hero.cta2}</BtnLink>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.95 }} className="mt-12 flex items-center gap-4 border-t border-forest/15 pt-6" data-testid="hero-founder-id">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-forest font-display text-lg italic text-gold-light">AK</span>
            <div>
              <p className="text-sm font-medium text-forest">{t.common.founder}</p>
              <p className="mt-0.5 flex items-center gap-1.5 text-sm text-forest/70"><MapPin size={13} /> {t.footer.location}</p>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease, delay: 0.2 }} className="relative lg:col-span-6">
          <div className="relative mx-auto w-full max-w-[640px]">
            <div className="absolute inset-[6%] rounded-full border border-gold/50" aria-hidden="true" />
            <motion.img
              style={{ y: imgY }}
              src="/img/ayse.webp"
              alt={`${t.common.photoLabel}, ${t.hero.imageAlt}`}
              width="1100"
              height="1005"
              fetchpriority="high"
              data-testid="hero-founder-photo"
              className="relative w-full [mask-image:linear-gradient(to_bottom,black_82%,transparent_100%)]"
            />
            <div className="absolute bottom-[4%] left-0 rounded-2xl bg-ivory-card/95 px-5 py-3 shadow-xl backdrop-blur sm:left-[2%]">
              <p className="font-display text-xl leading-none text-forest">Ayşegül Karaali</p>
              <p className="mt-1 text-xs uppercase tracking-[0.16em] text-gold-text">Izola · Slovenija</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
